import { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom, Vignette } from "@react-three/postprocessing";
import * as THREE from "three";
import { Flat } from "@/components/aiot/test/PlaceRoom";
import { HotelRoom, ROOM } from "@/components/aiot/lab/HotelRoom";
import { LabDevices, Spots } from "@/components/aiot/lab/LabDevices";
import { LabFx } from "@/components/aiot/lab/LabFx";
import { FactoryRoom } from "@/components/aiot/lab/FactoryRoom";
import { FactoryFx } from "@/components/aiot/lab/FactoryFx";

const SCENES = { home: Flat, hotel: HotelRoom, factory: FactoryRoom };

// Exposes a one-shot JPEG snapshot of the current view (used on the certificate).
const Snapper = ({ snapRef }) => {
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    if (!snapRef) return undefined;
    snapRef.current = () => { gl.render(scene, camera); return gl.domElement.toDataURL("image/jpeg", 0.9); };
    return () => { snapRef.current = null; };
  }, [gl, scene, camera, snapRef]);
  return null;
};

const Rig = ({ cam }) => {
  const { camera } = useThree();
  const tgt = useRef(new THREE.Vector3(...cam[1]));
  const v = useRef(new THREE.Vector3());
  useFrame(({ clock }, dt) => {
    const k = 1 - Math.exp(-dt * 0.9);
    const s = clock.elapsedTime * 0.25;
    v.current.set(cam[0][0] + Math.sin(s) * 0.4, cam[0][1] + Math.cos(s * 0.7) * 0.15, cam[0][2] + Math.cos(s) * 0.3);
    camera.position.lerp(v.current, k);
    tgt.current.lerp(new THREE.Vector3(...cam[1]), k);
    camera.lookAt(tgt.current);
  });
  return null;
};

const Env = ({ night, hot }) => {
  const amb = useRef(), dir = useRef();
  const { scene } = useThree();
  useFrame((_, dt) => {
    const k = 1 - Math.exp(-dt * 3);
    if (amb.current) amb.current.intensity += ((night ? 0.12 : 0.75) - amb.current.intensity) * k;
    if (dir.current) dir.current.intensity += ((night ? 0.15 : hot ? 2.2 : 1.5) - dir.current.intensity) * k;
    if (!scene.background) scene.background = new THREE.Color("#EAF2FB");
    const bg = scene.background;
    bg.lerp(new THREE.Color(night ? "#0B1220" : "#EAF2FB"), k);
    scene.background = bg;
  });
  return (
    <>
      <ambientLight ref={amb} intensity={0.75} />
      <directionalLight ref={dir} position={[4, 8, 5]} intensity={1.5} castShadow shadow-mapSize={[1024, 1024]} />
      <hemisphereLight args={["#DBEAFE", "#A16207", night ? 0.05 : 0.25]} />
    </>
  );
};

export const LabScene = ({ lab, placements, selected, onSpot, showSpots, fx, cam, cinematic, lang, snapRef }) => {
  const room = ROOM[lab.id];
  const Scene = SCENES[lab.id] || Flat;
  return (
    <Canvas shadows dpr={[1, 1.5]} camera={{ position: room.cam.intro[0], fov: 45 }} data-testid="lab-canvas">
      <Env night={fx.night} hot={fx.hot} />
      <Suspense fallback={null}>
        <Scene />
        <LabDevices lab={lab} placements={placements} selected={selected} broken={fx.broken} labels={!cinematic && !cam} lang={lang} />
        {showSpots && <Spots lab={lab} onPick={onSpot} active={!!selected} />}
        {lab.id === "factory" ? <FactoryFx room={room} fx={fx} /> : <LabFx room={room} fx={fx} />}
      </Suspense>
      <Snapper snapRef={snapRef} />
      {cam ? <Rig cam={cam} /> : <OrbitControls target={room.cam.intro[1]} enablePan={false} minDistance={4} maxDistance={14} maxPolarAngle={Math.PI / 2.1} />}
      {cinematic && (
        <EffectComposer multisampling={0}>
          <Bloom mipmapBlur intensity={0.9} luminanceThreshold={0.75} luminanceSmoothing={0.25} />
          <Vignette eskil={false} offset={0.2} darkness={0.75} />
        </EffectComposer>
      )}
    </Canvas>
  );
};
